import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  // Маршруты основного сайта
  {
    path: '/',
    name: 'main-home',
    component: () => import('../views/MainSiteView.vue'),
  },

  // ИСПРАВЛЕННЫЕ МАРШРУТЫ ДЛЯ МЕРОПРИЯТИЙ
  {
    path: '/events',
    name: 'events-list',
    component: () => import('../views/EventsListView.vue'),
  },
  {
    path: '/events/:slug',
    name: 'event-detail',
    component: () => import('../views/EventDetailView.vue'),
    props: true
  },

  // Страница входа
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: {
      requiresGuest: true // Только для неавторизованных
    }
  },

  // Группа маршрутов для админки
  {
    path: '/admin',
    name: 'admin-layout',
    component: () => import('../layouts/AdminLayout.vue'),
    meta: {
      requiresAuth: true // Требует авторизации
    },
    children: [
      {
        path: '',
        name: 'admin-home',
        component: () => import('../views/admin/HomeView.vue'),
      },

      // События
      {
        path: 'events',
        name: 'admin-events',
        component: () => import('../views/admin/EventsView.vue'),
      },
      {
        path: 'events/create',
        name: 'admin-events-create',
        component: () => import('../views/admin/EventCreateView.vue'),
      },
      {
        path: 'events/edit/:id',
        name: 'admin-events-edit',
        component: () => import('../views/admin/EventEditView.vue'),
        props: true
      },
      {
        path: 'partners',
        name: 'admin-partners',
        component: () => import('../views/admin/PartnersView.vue'),
        meta: {
          requiresSuperuser: true // Только для суперпользователей
        },
      },

      // Посты
      {
        path: 'posts',
        name: 'admin-posts',
        component: () => import('../views/admin/PostsView.vue'),
      },
      {
        path: 'posts/create',
        name: 'admin-posts-create',
        component: () => import('../views/admin/PostCreateView.vue'),
      },
      {
        path: 'posts/edit/:id',
        name: 'admin-posts-edit',
        component: () => import('../views/admin/PostEditView.vue'),
        props: true
      },

      // Пользователи - только для суперпользователей
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('../views/admin/UsersView.vue'),
        meta: {
          requiresSuperuser: true // Только для суперпользователей
        }
      },
      {
        path: 'users/create',
        name: 'admin-users-create',
        component: () => import('../views/admin/UserCreateView.vue'),
        meta: {
          requiresSuperuser: true
        }
      },
      {
        path: 'users/edit/:id',
        name: 'admin-users-edit',
        component: () => import('../views/admin/UserEditView.vue'),
        props: true,
        meta: {
          requiresSuperuser: true
        }
      },

      // Организации - только для суперпользователей
      {
        path: 'organizations',
        name: 'admin-organizations',
        component: () => import('../views/admin/OrganizationsView.vue'),
        meta: {
          requiresSuperuser: true
        }
      },
      {
        path: 'contact-messages',
        name: 'admin-contact-messages',
        component: () => import('../views/admin/ContactMessagesView.vue'),
        meta: {
          requiresSuperuser: true // Только для суперпользователей
        }
      },
    ]
  },

  // Catch all route
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/admin/NotFoundView.vue'),
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // ДОБАВЛЯЕМ ФУНКЦИЮ scrollBehavior
  scrollBehavior(to, from, savedPosition) {
    console.log('ScrollBehavior triggered:', { to: to.path, from: from.path, savedPosition })

    // Если есть сохраненная позиция (например, при использовании кнопки "Назад")
    if (savedPosition) {
      console.log('Restoring saved position:', savedPosition)
      return savedPosition
    }

    // Если есть хэш в URL (например, #section), прокручиваем к элементу
    if (to.hash) {
      console.log('Scrolling to hash:', to.hash)
      return {
        el: to.hash,
        behavior: 'smooth',
        top: 80 // Отступ для фиксированного хедера
      }
    }

    // По умолчанию - в начало страницы
    console.log('Scrolling to top')
    return {
      top: 0,
      left: 0,
      behavior: 'smooth' // Плавная прокрутка
    }
  }
})

// Навигационная защита
router.beforeEach(async (to, from, next) => {
  console.log('Router guard:', { to: to.path, from: from.path })

  const authStore = useAuthStore()

  // Ждем инициализации аутентификации если нужно
  if (!authStore.initialized) {
    console.log('Waiting for auth initialization...')

    let attempts = 0
    const maxAttempts = 10

    while (!authStore.initialized && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }

    if (!authStore.initialized) {
      console.warn('Auth initialization timeout')
    }
  }

  const isAuthenticated = authStore.isAuthenticated
  const isSuperuser = authStore.user?.is_superuser || false
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresSuperuser = to.matched.some(record => record.meta.requiresSuperuser)
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest)

  console.log('Auth check:', {
    isAuthenticated,
    isSuperuser,
    requiresAuth,
    requiresSuperuser,
    requiresGuest,
    user: authStore.user
  })

  // Если требует гостевой доступ (страница входа) и пользователь авторизован
  if (requiresGuest && isAuthenticated) {
    console.log('Redirecting authenticated user from guest page to admin')
    next('/admin')
    return
  }

  // Если требует авторизации и пользователь не авторизован
  if (requiresAuth && !isAuthenticated) {
    console.log('Redirecting unauthenticated user to login')
    next('/login')
    return
  }

  // Если требует права суперпользователя и пользователь не суперпользователь
  if (requiresSuperuser && !isSuperuser) {
    console.warn('Access denied: superuser required', { user: authStore.user })
    next('/admin') // Перенаправляем на главную админки
    return
  }

  console.log('Navigation allowed')
  next()
})

export default router
